// Parse a date value that may be either a date-only string ("YYYY-MM-DD")
// or a full ISO datetime. For date-only strings we construct the Date in
// LOCAL time so we never shift the calendar day through UTC (see
// timezone-shift bug fix — a fan in America/Los_Angeles must not see
// "August 21" as "August 20").
function _parseDateFlexible(value) {
    if (!value) return null;
    if (typeof value === "string") {
        const m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (m) {
            const [, y, mo, d] = m;
            return new Date(Number(y), Number(mo) - 1, Number(d));
        }
    }
    const d = new Date(value);
    return isNaN(d) ? null : d;
}

export function formatDate(iso) {
    const d = _parseDateFlexible(iso);
    if (!d) return iso || "";
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export function formatDateLong(iso) {
    const d = _parseDateFlexible(iso);
    if (!d) return iso || "";
    return d.toLocaleDateString(undefined, {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
    });
}

export function formatPrice(price, currency) {
    if (price === 0 || price === null || price === undefined) return "Free";
    const code = currency && String(currency).trim().toUpperCase();
    if (!code) return String(price);
    // Consistent Project Mint convention across Events + Business:
    // "ISO 5,000.00" — code first, always visible. Never rely on "$".
    const n = Number(price);
    if (Number.isNaN(n)) return `${code} ${price}`;
    return `${code} ${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

export function eventPriceRange(event) {
    if (event?.is_free) return "Free";
    const cats = event?.ticket_categories || [];
    if (!cats.length) return "—";
    const currency = event?.currency || (cats[0] && cats[0].currency) || "";
    const prices = cats.map((c) => c.price);
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    if (min === max) return formatPrice(min, currency);
    return `${formatPrice(min, currency)}–${formatPrice(max, currency)}`;
}

// Currencies Project Mint accepts + human labels, kept in sync with the
// backend `SUPPORTED_CURRENCIES` set. Used by the Event editor's picker.
export const SUPPORTED_CURRENCIES = [
    { code: "USD", label: "US Dollar" },
    { code: "ETB", label: "Ethiopian Birr" },
    { code: "ARS", label: "Argentine Peso" },
    { code: "COP", label: "Colombian Peso" },
    { code: "BRL", label: "Brazilian Real" },
    { code: "MXN", label: "Mexican Peso" },
    { code: "EUR", label: "Euro" },
    { code: "GBP", label: "British Pound" },
    { code: "PEN", label: "Peruvian Sol" },
    { code: "CLP", label: "Chilean Peso" },
    { code: "UYU", label: "Uruguayan Peso" },
    { code: "KES", label: "Kenyan Shilling" },
    { code: "NGN", label: "Nigerian Naira" },
    { code: "ZAR", label: "South African Rand" },
    { code: "GHS", label: "Ghanaian Cedi" },
    { code: "EGP", label: "Egyptian Pound" },
    { code: "MAD", label: "Moroccan Dirham" },
    { code: "CAD", label: "Canadian Dollar" },
    { code: "AUD", label: "Australian Dollar" },
    { code: "JPY", label: "Japanese Yen" },
    { code: "CNY", label: "Chinese Yuan" },
    { code: "INR", label: "Indian Rupee" },
];

// Frontend mirror of backend COUNTRY_TO_CURRENCY. Kept small (nothing
// exhaustive) — returns null when Project Mint cannot resolve the country
// so the editor can prompt the user rather than invent USD.
const COUNTRY_TO_CURRENCY = {
    "United States": "USD", "USA": "USD", "US": "USD",
    "Ethiopia": "ETB", "Argentina": "ARS", "Colombia": "COP",
    "Brazil": "BRL", "Mexico": "MXN",
    "United Kingdom": "GBP", "UK": "GBP",
    "France": "EUR", "Germany": "EUR", "Spain": "EUR", "Italy": "EUR",
    "Portugal": "EUR", "Netherlands": "EUR", "Belgium": "EUR", "Ireland": "EUR",
    "Peru": "PEN", "Chile": "CLP", "Uruguay": "UYU",
    "Kenya": "KES", "Nigeria": "NGN", "South Africa": "ZAR",
    "Ghana": "GHS", "Egypt": "EGP", "Morocco": "MAD",
    "Canada": "CAD", "Australia": "AUD", "Japan": "JPY",
    "China": "CNY", "India": "INR",
};

export function currencyForCountry(country) {
    if (!country) return null;
    return COUNTRY_TO_CURRENCY[String(country).trim()] || null;
}
